<template>
  <div v-click-outside="close" style="position: relative">
    <BaseInputText
      v-model="input"
      :placeholder="$t(placeholder)"
      :required="required"
      :name="name"
      no-autocomplete
      @click="focused = true"
      @keydown.escape.prevent="focused = false"
      @keydown.arrow-up.prevent="pointer--"
      @keydown.arrow-down.prevent="pointer++"
      @keydown.enter.prevent="selectPointer"
    />
    <span class="base-autocomplete">
      <div v-if="showOptions" class="menu">
        <div v-for="option in options" :key="option.keyName" class="dataset">
          <template v-if="activeSets[option.keyName] && activeSets[option.keyName].length > 0">
            <h3 class="name">{{ $t(option.title) }}</h3>
            <transition-group :duration="{ enter: 600, leave: 600 }" name="list-complete" tag="div">
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-for="item in activeSets[option.keyName]"
                :key="item"
                class="suggestion selectable list-complete-item"
                :class="{
                  highlight: item === pointerHighlight && option.keyName === pointerOption,
                }"
                @click="input = item"
                v-html="highlight(item)"
              ></div>
              <!-- eslint-enable vue/no-v-html -->
            </transition-group>
          </template>
        </div>
      </div>
    </span>
  </div>
</template>

<script>
import { debounce } from '@/utils/tools'
import BaseInputText from '@/components/BaseInputText.vue'

/**
 * Text input with a dropdown of searchable options
 * Supports highlighting of string matches found within the options
 * Supports v-model
 */
export default {
  name: 'InputTextAutocomplete',
  components: { BaseInputText },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    //  HTML <input> name attribute
    name: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    minSearchLength: {
      type: Number,
      default: 3,
    },
    // The max number of search results to display from each option
    limit: {
      type: Number,
      default: 10,
    },
    /**
     * Array of objects where each object can have these parameters
     * {
     *   title: title that gets displayed in the dropdown
     *   keyName: key name of the dataset
     *   data: array of string options
     * }
     */
    options: {
      type: Array,
      default() {
        return []
      },
      validator(arrOpts) {
        if (!arrOpts) return true
        if (!(arrOpts instanceof Array)) return false
        return arrOpts?.every((opts) => {
          const requiredProps = typeof opts.title === 'string' && typeof opts.keyName === 'string'
          return opts.data instanceof Array && requiredProps
        })
      },
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      activeSets: {},
      currentValue: this.modelValue,
      // Debounced version of search
      debounceSearch: debounce(this.search, 100),
      pointerData: -1,
      focused: false,
    }
  },
  computed: {
    showOptions() {
      return this.focused
    },
    input: {
      get() {
        return this.currentValue
      },
      set(val) {
        this.focused = true
        this.currentValue = val
        this.$emit('update:modelValue', val)
        this.pointer = -1
        this.debounceSearch(this.currentValue)
      },
    },
    pointer: {
      get() {
        return this.pointerData
      },
      set(val) {
        let shown = 0
        for (const [_, value] of Object.entries(this.activeSets)) {
          shown += value.length
        }

        this.pointerData = Math.min(Math.max(-1, val), shown - 1)
      },
    },
    pointerHighlight() {
      let vals = []
      for (const [_, value] of Object.entries(this.activeSets)) {
        vals = vals.concat(value)
      }
      return vals[this.pointer]
    },
    pointerOption() {
      let copy = this.pointer
      for (const [i, vals] of Object.entries(this.activeSets)) {
        copy -= vals.length
        if (copy < 0) {
          return i
        }
      }
      return -1
    },
  },
  watch: {
    modelValue() {
      this.currentValue = this.modelValue
    },
  },
  methods: {
    /**
     * @description
     *    This finds the searched for string and constructs the activeSets used to display in the options dropdown
     * @param {string} val
     *    The string we wish to search for in our options
     * @affects
     *   this.activeSets
     */
    search(val) {
      const text = val.toLowerCase()
      const res = {}
      if (text.length >= this.minSearchLength) {
        for (const opt of this.options) {
          res[opt.keyName] = opt.data
            .filter((v) => v.toLowerCase().includes(text) && v.toLowerCase() !== text)
            .slice(0, this.limit)
        }
      }
      this.activeSets = res
    },
    /**
     * @param {string} val
     *    The string we wish to highlight with <strong>
     * @return {string}
     *    returns the string with <strong> around the searched area
     */
    highlight(val) {
      const len = this.currentValue.length
      if (len <= 0) return val
      let startIndex = 0
      let index = []
      const indices = []
      const str = val.toLowerCase()
      const searchStr = this.currentValue.toLowerCase()
      while ((index = str.indexOf(searchStr, startIndex)) > -1) {
        indices.push(index)
        startIndex = index + len
      }
      const copy = val.split('')
      let counter = 0
      for (const ind of indices) {
        const part = val.slice(ind, ind + len)
        copy.splice(ind - counter * (len - 1), len, `<strong>${part}</strong>`)
        counter += 1
      }

      return copy.join('')
    },
    /**
     * @description
     *    Closes the dropdown
     * @affects
     *    this.focused
     */
    close() {
      this.focused = false
    },
    /**
     * @description
     *    Sets the input to the currently selected pointer and then closes the dropdown afterwards
     * @affects
     *    this.input
     */
    selectPointer() {
      if (this.pointer > -1) {
        this.input = this.pointerHighlight
        this.close()
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';

.base-autocomplete {
  .hints {
    position: absolute;
    top: 0px;
    left: 0px;
    border-color: transparent;
    @apply tw-shadow-none;
    opacity: 1;
    background: none 0% 0% / auto repeat scroll padding-box border-box rgb(244, 247, 251);
    height: 38px;
  }

  .menu {
    min-width: 100%;
    padding: 0 10px;
    background: #fff;
    @apply tw-shadow;
    position: absolute;
    left: 0px;
    z-index: 100;
    .suggestion {
      font-size: 18px;
      padding: 3px 18px;
      line-height: 24px;
      font-weight: normal;
    }
    .selectable {
      cursor: pointer;
      &:hover {
        background-color: #0097cf;
        color: white;
      }
    }
    .highlight {
      background-color: #0097cf;
      color: white;
    }
    .name {
      padding: 0 18px;
      font-size: 16px;
      color: #6772e5;
    }
  }
}

.list-complete-item {
  transition: all 0.6s ease, background-color 0ms, color 0ms;
}
.list-complete-enter-from,
.list-complete-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
.list-complete-leave-active {
  position: absolute;
  width: 100%;
  max-width: 100%;
  display: block;
}
</style>
